import { createSelector } from 'reselect';
import jwt from 'jsonwebtoken';
import * as roles from '../../constants/roles';

const { OWNER, ADMIN, BILLING, OR_ADMIN, OR_SUPERUSER, OR_READONLY } = roles;
// Add these roles above ass you need them : METRICS, READONLY, OR_SUPERREAD,

/**
 * Returns the complete user state
 *
 * @param {object} state - Current state
 * @returns {object} - The entire user state
 */
export const getUserState = (state) => state.user;

/**
 * Returns the session portion of the user state
 *
 * @param {object} state - Current state
 * @returns {object} - The session state
 */
export const getSessionState = (state) => state.user.session;

/**
 * Returns the credentials portion of the user state
 *
 * @param {object} state - Current state
 * @returns {object} - The credentials state
 */
export const getCredentialsState = (state) => state.user.credentials;

/**
 * Get `exp` field from access token.
 *
 * @param {object} state - Current state
 * @returns {number} - UTC (in ms) in which the accessToken is set to expire.
 */
export const getTokenExpirationDate = createSelector(getCredentialsState, (creds) =>
  creds.accessToken ? jwt.decode(creds.accessToken).exp * 1000 : null,
);

/**
 * Get `iat` field from access token.
 *
 * @param {object} state - Current state
 * @returns {number} - UTC (in ms) in which the accessToken was issued at.
 */
export const getTokenStartDate = createSelector(getCredentialsState, (creds) =>
  creds.accessToken ? jwt.decode(creds.accessToken).iat * 1000 : null,
);

/**
 * Returns the feature flags portion of the user state
 *
 * @param {object} flags - Raw map of feature flags for this user
 * @returns {object} - Map of feature flags, with each flag's current value
 */
const removeFlagHistory = (flags) =>
  Object.keys(flags).reduce((newFlags, key) => {
    const updatedFlags = { ...newFlags };
    let val = flags[key];
    val = val && Object.prototype.hasOwnProperty.call(val, 'current') ? val.current : val;
    updatedFlags[key] = val;
    return updatedFlags;
  }, {});

/**
 * Returns the feature flags portion of the user state
 *
 * @param {object} state - Current state
 * @param {boolean} stripHistory - Whether or not to strip history (i.e. previous
 *  and current properties) for each flag if known.
 *  If true, will provide an object where are values of each key is the current
 *  value of the flag.
 *  If false, each flag may be represented as a single value or an object
 *  containing the current and previous values (if the history is known):
 *  {current: newValue, previous: oldValue}
 * @returns {object} - The feature flags state with appropriate history included
 */
export const getFeatureFlagsState = (state, stripHistory = false) =>
  stripHistory
    ? { ...state.user.featureFlags, flags: removeFlagHistory(state.user.featureFlags.flags) }
    : state.user.featureFlags;

/**
 * Returns the url to use for the create instance flow
 *
 * @param {object} state - Current state
 * @returns {string} - The URL for the create flow.
 */
export const createStringSelector = (state) => {
  const flags = removeFlagHistory(state.user.featureFlags.flags);
  if (typeof flags.create === 'undefined') {
    return '/mission-ctrl/create-instance';
  }
  return flags.create;
};

/**
 * Get identity-related information (name, email, etc) for the user
 *
 * @param {object} state - Current state
 * @returns {object} - The identity state for current user
 */
export const getIdentityState = (state) => ({
  featureFlags: getFeatureFlagsState(state).flags,
  ...state.user.identity,
});

/**
 * Determine whether user is logged in with a valid, unexpired session
 *
 * @param {object} state - Current state
 * @returns {boolean} - Whether or not user is authenticated
 */
export const isAuthenticated = createSelector(getTokenExpirationDate, (expirationDate) => () =>
  expirationDate > Date.now(),
);

/**
 * Determine whether user is associated with an organization
 *
 * @param {object} state - Current state
 * @returns {boolean} - Whether or not user is associated with an organization
 */
export const needsOrganization = (state) => !getIdentityState(state).organizationId;

/**
 * Determine whether user's email has been verified
 *
 * @param {object} state - Current state
 * @returns {boolean} - Whether or not user's email has been verified
 */
export const needsEmailVerification = (state) => !getIdentityState(state).emailVerified;

/**
 * Determines whether or not a user is an admin
 *
 * @param {object} state - Current state
 * @returns {boolean} - Whether or not a user is an admin
 */
const ADMINS = [OR_ADMIN, OR_READONLY];

export const isAdmin = (state) =>
  ADMINS.some((role) => getIdentityState(state).roles.indexOf(role) !== -1);

const CUSTOMERADMINS = [OWNER, ADMIN];

export const isCustomerAdmin = (state) =>
  CUSTOMERADMINS.some((role) => getIdentityState(state).roles.indexOf(role) !== -1);

const BILLINGUSERS = [OWNER, BILLING, OR_ADMIN, OR_SUPERUSER];

export const isBillingUser = (state) =>
  BILLINGUSERS.some((role) => getIdentityState(state).roles.indexOf(role) !== -1);

export const isOwner = (state) =>
  [OWNER].some((role) => getIdentityState(state).roles.indexOf(role) !== -1);

export const isORv4User = (state) =>
  isAdmin(state) || isOwner(state) || typeof state.user.identity.ORv4ID !== 'undefined';

// Objectrocket admins can login to v4 using the SSO link
const ORV4ADMINS = [OWNER, ADMIN, OR_ADMIN]

export const isORv4Admin = (state) => {
  const hasV4accessRoles = ORV4ADMINS.some((role) => getIdentityState(state).roles.indexOf(role) !== -1);
  
  // Org and User v4 account must be same.
  const hasDedicatedAccess = Boolean(state.org.orv4id) && Boolean(state.user.identity.ORv4ID) && 
    state.org.orv4id === state.user.identity.ORv4ID;

  return hasV4accessRoles && hasDedicatedAccess
}
  
